import { CheckCircle } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { RadioButton, RadioGroup } from '@/lib/base/radio-buttons';
import { Wizard, type WizardStep } from '@/lib/base/wizard';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';

import { useMailchimpMappings } from '../hooks/useMailchimpMappings';
import { useMailchimpStatus } from '../hooks/useMailchimpStatus';
import type { MailchimpFieldMapping } from '../types';
import { ConnectMailchimpButton } from './ConnectMailchimpButton';
import { WizardAudienceStep } from './wizard/WizardAudienceStep';
import { WizardFieldsStep } from './wizard/WizardFieldsStep';

interface MailchimpSetupWizardProps {
  orgId: number | string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Step to open on (computed from connection status by the parent). */
  defaultStepId: string;
  /** Called after setup-complete succeeds, so the parent can show the status panel. */
  onFinished: () => void;
}

const EMPTY_MAPPINGS: MailchimpFieldMapping[] = [];

// One content width for the whole wizard so the body column AND the footer CTAs sit
// in the same place on every step (the Fields step needs ~900px for its mapping
// tables; the narrower steps just center within it). Hoisted to module scope so the
// object reference is stable across renders.
const WIZARD_FULLSCREEN_PROPS = { maxContentWidth: 900 } as const;

export function MailchimpSetupWizard({
  orgId,
  isOpen,
  onOpenChange,
  defaultStepId,
  onFinished,
}: MailchimpSetupWizardProps) {
  const status = useMailchimpStatus(orgId);
  const connected = status.data?.connected ?? false;
  const hasAudience = !!status.data?.audience_id;

  // Only fetch field mappings once an audience exists (on_connect creates them) and
  // the wizard is actually open. Fetching earlier (just on orgId) caches an empty
  // result that makes the Fields step show "No field mappings found"; gating here ties
  // the fetch to the lifecycle that produces the data and avoids wasted fetches while
  // the wizard is closed.
  const { mappings, updateMapping, completeSetup, createMergeField } =
    useMailchimpMappings(orgId, {
      enabled: isOpen && connected && hasAudience,
    });
  const { schema: propertySchema } = usePropertySchema(orgId, 'ahoy_contact');

  const availableAhoyProperties = useMemo(
    () =>
      Object.values(propertySchema ?? {})
        .filter(
          p =>
            p.value_write_access !== 'none' &&
            p.value_write_access !== 'internal'
        )
        .map(p => ({
          name: p.property_type_name,
          label: p.label,
          fieldType: p.field_type_name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [propertySchema]
  );

  const allMappings = mappings.data?.mappings ?? EMPTY_MAPPINGS;
  const [backfill, setBackfill] = useState<'yes' | 'no'>('yes');
  const [finishError, setFinishError] = useState<string | null>(null);

  const savingId =
    updateMapping.isPending && updateMapping.variables
      ? updateMapping.variables.mappingId
      : null;

  const handleMappingUpdate = useCallback(
    (
      mappingId: number,
      body: Parameters<typeof updateMapping.mutate>[0]['body']
    ) => {
      updateMapping.mutate({ mappingId, body });
    },
    [updateMapping]
  );

  const creatingProp =
    createMergeField.isPending && createMergeField.variables
      ? createMergeField.variables.ahoy_property
      : null;

  const handleCreateMergeField = useCallback(
    (body: Parameters<typeof createMergeField.mutate>[0]) => {
      createMergeField.mutate(body);
    },
    [createMergeField]
  );

  const handleBackfillChange = useCallback((value: string) => {
    setBackfill(value === 'no' ? 'no' : 'yes');
  }, []);

  const handleFinish = useCallback(async () => {
    setFinishError(null);
    try {
      await completeSetup.mutateAsync(backfill === 'yes');
    } catch {
      setFinishError(
        'We couldn’t finish setup. Your field mappings are saved. Try again.'
      );
      throw new Error('completeSetup failed'); // keep the wizard on this step
    }
    onFinished();
  }, [completeSetup, backfill, onFinished]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const steps = useMemo<WizardStep[]>(() => {
    return [
      {
        id: 'connect',
        label: 'Connect',
        // The body owns its CTAs: a redirect button before OAuth, then an
        // audience picker that calls ctx.next() once an audience is chosen.
        hideFooter: true,
        render: () =>
          !connected ? (
            <div className='flex flex-col items-center'>
              <ConnectMailchimpButton orgId={orgId} />
            </div>
          ) : (
            <WizardAudienceStep orgId={orgId} />
          ),
      },
      {
        id: 'fields',
        label: 'Fields',
        title: 'Map your fields',
        subtitle:
          'Choose how each Mailchimp field maps to an Ahoy contact property.',
        hideBackButton: true,
        render: () => (
          <WizardFieldsStep
            mappings={allMappings}
            availableAhoyProperties={availableAhoyProperties}
            savingId={savingId}
            creatingProp={creatingProp}
            // Show loading (never the empty "No field mappings found" state) while
            // anything is still settling: the audience isn't resolved yet, the query
            // is loading, OR a fetch is in flight and we have no mappings yet (a
            // background refetch over an empty/stale cache reports isLoading=false).
            isLoading={
              mappings.isLoading ||
              !hasAudience ||
              (mappings.isFetching && allMappings.length === 0)
            }
            onUpdate={handleMappingUpdate}
            onCreateMergeField={handleCreateMergeField}
          />
        ),
        // Disable Next while mappings aren't ready: still loading, no audience
        // selected yet, or the cached list is empty (e.g. mapping fetch errored
        // or returned []). Uses `isLoading` not `isFetching` so background
        // refetches over a populated cache don't disable advancement.
        canAdvance: () =>
          !mappings.isLoading && hasAudience && allMappings.length > 0,
        primaryCta: { label: 'Next' },
      },
      {
        id: 'import',
        label: 'Import',
        title: 'Sync existing contacts now?',
        subtitle: 'This only affects contacts that already exist.',
        hideBackButton: true,
        render: () => (
          <div className='flex flex-col gap-3'>
            <RadioGroup
              value={backfill}
              onChange={handleBackfillChange}
              className='gap-3'
            >
              <RadioButton
                value='yes'
                label='Yes, bring everyone in sync'
                hint='Import this audience into Ahoy and add your existing Ahoy contacts to Mailchimp.'
              />
              <RadioButton
                value='no'
                label='No, only sync changes from now on'
                hint='Nothing is imported or pushed; only future edits sync.'
              />
            </RadioGroup>
            {finishError && (
              <p
                role='alert'
                className='text-body-small text-[var(--color-text-error-primary)]'
              >
                {finishError}
              </p>
            )}
          </div>
        ),
        primaryCta: {
          label: 'Finish setup',
          onClick: async ctx => {
            await handleFinish();
            ctx.next();
          },
        },
      },
      {
        id: 'done',
        label: 'Done',
        hideFooter: true,
        render: () => (
          <div className='flex flex-col items-center gap-4 py-6 text-center'>
            <FeaturedIcon
              icon={CheckCircle}
              color='success'
              theme='light-outline'
              size='lg'
            />
            <div>
              <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
                You’re all set
              </h3>
              <p className='mt-1 max-w-md text-body-small text-[var(--color-text-secondary)]'>
                {backfill === 'yes'
                  ? 'We’re importing your audience and syncing existing contacts now. New changes will keep syncing both ways.'
                  : 'Mailchimp is connected. New changes will sync both ways from now on.'}
              </p>
            </div>
            <Button color='primary' size='md' onClick={handleClose}>
              Go to Mailchimp settings
            </Button>
          </div>
        ),
      },
    ];
  }, [
    connected,
    hasAudience,
    orgId,
    allMappings,
    availableAhoyProperties,
    savingId,
    creatingProp,
    mappings.isLoading,
    mappings.isFetching,
    handleMappingUpdate,
    handleCreateMergeField,
    backfill,
    handleBackfillChange,
    handleFinish,
    handleClose,
    finishError,
  ]);

  return (
    <Wizard
      mode='fullscreen'
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      steps={steps}
      defaultStepId={defaultStepId}
      fullscreenProps={WIZARD_FULLSCREEN_PROPS}
      dialogAriaLabel='Set up Mailchimp'
      instance='mailchimp-setup-wizard'
      discardConfirmation={false}
    />
  );
}
